export const MAX_GPX_BYTES = 10 * 1024 * 1024;
export const ELEVATION_NOISE_THRESHOLD_METERS = 3;
export const SMOOTHING_WINDOW_POINTS = 5;

export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

export function haversine(a, b) {
  const earthRadiusKm = 6371;
  const dLat = toRadians(b.latitude - a.latitude);
  const dLon = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

  return 2 * earthRadiusKm * Math.asin(Math.sqrt(h));
}

export function getSpeedColor(speed, minSpeed, maxSpeed) {
  const range = maxSpeed - minSpeed;
  const ratio = range > 0.001 ? clamp((speed - minSpeed) / range, 0, 1) : 0.5;
  const hue = Math.round(ratio * 120);

  return `hsl(${hue}, 88%, 46%)`;
}

export function percentile(values, percent) {
  const sorted = values.slice().sort((a, b) => a - b);
  if (!sorted.length) {
    return 0;
  }

  const index = (sorted.length - 1) * percent;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);

  if (lower === upper) {
    return sorted[lower];
  }

  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

export function getTicks(min, max, count) {
  const ticks = [];
  const step = (max - min) / Math.max(count - 1, 1);
  for (let index = 0; index < count; index += 1) {
    ticks.push(min + step * index);
  }
  return ticks;
}

export function thinPoints(points, maxPoints) {
  if (points.length <= maxPoints) {
    return points;
  }

  const step = Math.ceil(points.length / maxPoints);
  const thinned = points.filter((_, index) => index % step === 0);
  thinned.push(points[points.length - 1]);
  return thinned;
}

export function parseCssPixels(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 240 ? parsed : fallback;
}

export function formatDistance(value) {
  return `${round(value).toLocaleString()} km`;
}

export function formatDistanceValue(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function formatDuration(seconds) {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`;
  }

  return `${remainingSeconds}s`;
}

export function formatElevation(value) {
  return `${Math.round(value).toLocaleString()} m`;
}

export function formatSpeed(value) {
  return `${round(value)} km/h`;
}

export function round(value) {
  return Math.round(value * 10) / 10;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => fn.apply(this, args), wait);
  };
}

function toRadians(value) {
  return value * Math.PI / 180;
}
